import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * ScrollColumn — VERBATIM port from ~/FrontendFinal/Design/src/components/ScrollColumn.tsx.
 *
 * P4's right column scrolls, and the vertical line the frame draws down the middle
 * IS that column's scrollbar — a two-tone scroll-progress indicator (faint track +
 * solid thumb), not a static divider. This renders a fixed-height viewport that
 * scrolls its overflowing `children` with the native bar hidden, plus a separate
 * scrollbar element (a sibling, so the page can drop it into the grid's rule track)
 * whose thumb height and offset are computed live from the scroll state. See the
 * Design repo's header for the full derivation. Imports only React — no repoint.
 */

export interface ScrollColumnProps {
  /** The scrolling content (the read-back form). */
  children: ReactNode;
  /**
   * The custom two-tone scrollbar, rendered as a sibling so the page can drop it
   * into the grid's rule track. Handed the live metrics.
   */
  scrollbar: (metrics: ScrollMetrics) => ReactNode;
  /** Height utility for the viewport, e.g. `h-review-card`. */
  viewportClassName?: string;
  className?: string;
}

export interface ScrollMetrics {
  /** track height / thumb height as a fraction (0..1] — thumb is this × track. */
  thumbFraction: number;
  /** thumb top offset as a fraction of the free track space (0..1). */
  offsetFraction: number;
  /** true when the content overflows and the column can actually scroll. */
  scrollable: boolean;
}

export function ScrollColumn({
  children,
  scrollbar,
  viewportClassName = '',
  className = '',
}: ScrollColumnProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    thumbFraction: 1,
    offsetFraction: 0,
    scrollable: false,
  });

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight - clientHeight;
    if (overflow <= 0 || clientHeight === 0) {
      setMetrics({ thumbFraction: 1, offsetFraction: 0, scrollable: false });
      return;
    }
    setMetrics({
      // Thumb height = viewport as a fraction of content. Smaller content-vs-
      // viewport ratio -> shorter thumb (the frame's short thumb = tall content).
      thumbFraction: clientHeight / scrollHeight,
      // Position along the free track = how far through the overflow we are.
      offsetFraction: scrollTop / overflow,
      scrollable: true,
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // Content or viewport resizing changes the ratios (responsive, font load).
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [measure]);

  return (
    <>
      {/* The scrollbar first — the page places it in the grid's rule track, which
          sits to the LEFT of this viewport in source order (rule | form). */}
      {scrollbar(metrics)}

      {/* The viewport: fixed height, scrolls its overflow, native bar hidden. */}
      <div
        ref={viewportRef}
        className={['min-h-0 overflow-y-auto scrollbar-none', viewportClassName, className].join(
          ' ',
        )}
      >
        {children}
      </div>
    </>
  );
}
